import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { logger } from '@/shared/utils/logger';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { CreateCustomerUserData, EMAIL_REGEX } from '@/shared/types/userTypes';
import { Role, getRoleName } from '@/shared/types/userRoleTypes';
import { useCreateCustomerUser } from '@/domains/customers/services/account-customers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { useUserRoles } from '@/shared/hooks/userRoles';

interface CreateCustomerUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
}

const CreateCustomerUserDialog: React.FC<CreateCustomerUserDialogProps> = ({
  isOpen,
  onClose,
  customerId,
}) => {
  const { customerUserRoles } = useUserRoles();

  const [formData, setFormData] = useState<CreateCustomerUserData>({
    name: '',
    email: '',
    role: customerUserRoles[0] || Role.VIEWER,
  });
  const createCustomerUser = useCreateCustomerUser();
  const [errors, setErrors] = useState<Partial<Record<keyof CreateCustomerUserData, string>>>({});

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        email: '',
        role: customerUserRoles[0] || Role.VIEWER,
      });
      setErrors({});
    }
  }, [isOpen, customerUserRoles]);

  const handleCreateUser = async (userData: CreateCustomerUserData) => {
    try {
      await createCustomerUser.mutateAsync({ customerId, userData });

      // Reset form on successful registration
      setFormData({
        name: '',
        email: '',
        role: customerUserRoles[0] || Role.VIEWER,
      });
      setErrors({});
      onClose();
      showSuccess(SUCCESS.USER.CREATED);
    } catch (error) {
      showErrorWithApiError(error, ERROR.USER.CREATE);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateCustomerUserData, string>> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Role is required';
    } else if (!customerUserRoles.some((role) => role === formData.role)) {
      newErrors.role = 'Please select a valid role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    handleCreateUser(formData);
  };

  const handleInputChange = (field: keyof CreateCustomerUserData, value: string | Role) => {
    setFormData(prev => {
      return { ...prev, [field]: value };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      role: customerUserRoles[0] || Role.VIEWER,
    });
    setErrors({});
    onClose();
  };

  const canSubmit = !createCustomerUser.isPending && formData.name.trim() && formData.email.trim() && customerUserRoles.some((role) => role === formData.role);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
          <DialogDescription>
            Enter your information to create a new user. All fields are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Role Field */}
          <div className="space-y-2">
            <Label htmlFor="role">User Role *</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleInputChange('role', value as Role)}
            >
              <SelectTrigger className="w-full" id="role">
                <SelectValue placeholder="Select User Role" />
              </SelectTrigger>
              <SelectContent>
                {customerUserRoles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {getRoleName(role)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && (
              <p className="text-sm text-red-500">{errors.role}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createCustomerUser.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
            >
              {createCustomerUser.isPending ? 'Creating User...' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCustomerUserDialog; 